import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import TrustComplianceSection from "./TrustComplianceSection";
import BookDemo from "./BookDemo";
import BrandIntro, { hasSeenIntro } from "./BrandIntro";
import { SiteNav, SiteFooter, NAV_SECTIONS } from "./SiteChrome";
import DashboardWaveform from "./WaveformBackground";
// works
const globalStyle = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&family=Poppins:wght@400;500;600&family=DM+Sans:wght@300;400;500&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --teal: #2bb3a3;
    --indigo: #6366f1;
    --bg: #f0f9ff;
    --dark: #0a0f1e;
    --text: #1e293b;
    --muted: #64748b;
    --nav-height: 116px;
  }

  h1, h2, h3, h4, h5, h6 {
    font-family: 'Inter', 'DM Sans', sans-serif;
  }
  p, span, div, button, input, select, label {
    font-family: inherit;
  }

  html { scroll-behavior: smooth; }
  #home, #platform, #insights, #trust, #contact {
    scroll-margin-top: var(--nav-height);
  }
  body {
    font-family: 'Inter', 'DM Sans', sans-serif;
    background: var(--bg);
    color: var(--text);
    overflow-x: hidden;
  }

  #root, #__next {
    display: block !important;
    width: 100% !important;
    overflow-x: hidden !important;
  }

  .sentio-app {
    display: block !important;
    width: 100% !important;
    max-width: 100vw !important;
    overflow-x: hidden !important;
  }

  #demo-page-root {
    position: fixed;
    inset: 0;
    z-index: 10001;
    overflow-x: hidden;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    background: #f0f9ff;
  }

  ::selection { background: rgba(43,179,163,0.25); }

  ::-webkit-scrollbar { width: 6px; }
  ::-webkit-scrollbar-track { background: #e0f2fe; }
  ::-webkit-scrollbar-thumb { background: var(--teal); border-radius: 3px; }

  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(32px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes fadeIn {
    from { opacity: 0; } to { opacity: 1; }
  }
  @keyframes pulseRing {
    0%   { transform: scale(1);   opacity: 0.6; }
    100% { transform: scale(2.2); opacity: 0; }
  }
  @keyframes scanLine {
    0%   { top: 8%;  }
    50%  { top: 72%; }
    100% { top: 8%;  }
  }
  @keyframes glowPulse {
    0%,100% { box-shadow: 0 0 20px rgba(43,179,163,0.3), 0 0 60px rgba(99,102,241,0.1); }
    50%      { box-shadow: 0 0 40px rgba(43,179,163,0.6), 0 0 80px rgba(99,102,241,0.25); }
  }
  @keyframes barGrow {
    from { width: 0; }
    to   { width: var(--w); }
  }
  @keyframes shimmer {
    0%   { background-position: -200% center; }
    100% { background-position: 200% center; }
  }
  @keyframes heatPulse {
    0%,100% { opacity: 0.7; }
    50%      { opacity: 1; }
  }
  @keyframes liveDot {
    0%,100% { opacity: 1; }
    50%      { opacity: 0.3; }
  }


  .reveal {
    opacity: 0;
    transform: translateY(28px);
    transition: opacity 0.7s ease, transform 0.7s ease;
  }
  .reveal.visible {
    opacity: 1;
    transform: translateY(0);
  }

  /* ── HERO ── */
  .hero {
    display: block;
    width: 100%;
    min-height: 100vh;
    padding: 140px 40px 80px;
    text-align: center;
    position: relative;
    overflow: hidden;
    background: var(--bg);
  }
  .hero-inner {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: calc(100vh - 220px);
  }
  .hero-bg-orb {
    position: absolute; border-radius: 50%; filter: blur(80px); pointer-events: none;
  }
  .hero-tag {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 7px 18px; border-radius: 100px;
    border: 1.5px solid rgba(43,179,163,0.35);
    background: rgba(43,179,163,0.07);
    font-size: 12px; font-weight: 500; color: #2bb3a3;
    letter-spacing: 1px; text-transform: uppercase;
    margin-bottom: 28px;
    animation: fadeIn 0.8s ease both;
  }
  .hero-tag-dot {
    width: 7px; height: 7px; border-radius: 50%;
    background: #2bb3a3;
    position: relative;
    flex-shrink: 0;
  }
  .hero-tag-dot::before {
    content: ''; position: absolute; inset: -3px; border-radius: 50%;
    border: 1.5px solid rgba(43,179,163,0.5);
    animation: pulseRing 1.5s ease-out infinite;
  }
  .hero-coming-soon {
    position: absolute;
    top: 165px; right: 40px;
    z-index: 3;
    display: inline-flex; align-items: center; gap: 8px;
    padding: 8px 18px; border-radius: 100px;
    border: 1.5px solid rgba(99,102,241,0.35);
    background: rgba(255,255,255,0.75);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    box-shadow: 0 4px 16px rgba(15,23,42,0.08);
    font-size: 12px; font-weight: 600; color: #4f46e5;
    letter-spacing: 1.2px; text-transform: uppercase;
    white-space: nowrap;
    animation: fadeIn 0.8s ease 0.3s both;
  }
  .hero-coming-soon-icon {
    width: 14px; height: 14px;
    flex-shrink: 0;
    stroke: #6366f1;
  }
  .hero h1 {
    font-family: 'Poppins', sans-serif;
    font-size: clamp(38px, 5.5vw, 85px);
    font-weight: 800; line-height: 1.1;
    letter-spacing: -0.5px;
    max-width: 60%;
    animation: fadeUp 0.9s ease 0.2s both;
  }
  .hero-gradient-text {
    will-change: transform;
    transform: translateZ(0);
    background: linear-gradient(135deg, #2bb3a3 0%, #6366f1 60%, #a78bfa 100%);
    -webkit-background-clip: text; -webkit-text-fill-color: transparent;
    background-clip: text;
    background-size: 200% auto;
    animation: shimmer 4s linear infinite;
  }
  .hero p {
    font-size: 30px; color: #64748b; max-width: 60%;
    line-height: 1.7; margin-top: 20px;
    animation: fadeUp 0.9s ease 0.35s both;
  }
  .hero-cta-group {
    display: flex; gap: 16px; margin-top: 40px;
    animation: fadeUp 0.9s ease 0.5s both;
    flex-wrap: wrap; justify-content: center;
  }
  .btn-primary {
    padding: 18px 32px; border-radius: 100px; border: none; cursor: pointer;
    font-family: 'DM Sans', sans-serif; font-size: 20px; font-weight: 600;
    background: linear-gradient(90deg, #2bb3a3, #6366f1);
    color: white; letter-spacing: 0.3px;
    transition: all 0.3s;
    position: relative; overflow: hidden;
    min-height: 52px;
  }
  .btn-primary::after {
    content: ''; position: absolute; inset: 0;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent);
    transform: translateX(-100%); transition: transform 0.5s;
  }
  .btn-primary:hover { transform: translateY(-3px); box-shadow: 0 12px 35px rgba(43,179,163,0.4); }
  .btn-primary:hover::after { transform: translateX(100%); }
  .btn-ghost {
    padding: 18px 32px; border-radius: 100px; cursor: pointer;
    font-family: 'DM Sans', sans-serif; font-size: 20px; font-weight: 600;
    background: transparent; color: #475569;
    border: 1.5px solid rgba(43,179,163,0.35);
    transition: all 0.3s;
    min-height: 52px;
  }
  .btn-ghost:hover { border-color: #2bb3a3; color: #2bb3a3; background: rgba(43,179,163,0.05); }

  .hero-stats {
    display: flex; gap: 40px; margin-top: 60px;
    animation: fadeUp 0.9s ease 0.65s both;
    flex-wrap: wrap; justify-content: center;
  }
  .hero-stat-item { text-align: center; }
  .hero-stat-num {
    font-family: 'Inter', sans-serif; font-size: 40px; font-weight: 800;
    background: linear-gradient(90deg, #2bb3a3, #6366f1);
    line-height: 1.3;
    -webkit-background-clip: text; -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  .hero-stat-label { font-size: 16px; color: #94a3b8; text-transform: uppercase; letter-spacing: 1px; margin-top: 2px; }

  /* ── SECTION ── */
  .section {
    display: block;
    width: 100%;
    padding: 100px 60px;
    position: relative;
  }
  .section-label {
    display: inline-block;
    font-family: 'Inter', sans-serif;
    font-size: 12px; font-weight: 500; letter-spacing: 2.5px;
    text-transform: uppercase; color: #2bb3a3;
    margin-bottom: 14px;
  }
  .section-title {
    font-family: 'Inter', sans-serif;
    font-size: clamp(36px, 3.5vw, 46px); font-weight: 800;
    letter-spacing: -1.5px; line-height: 1.15;
    color: #0f172a;
  }
  .section-subtitle {
    font-size: 22px; color: #64748b;
    max-width: 520px; line-height: 1.7; margin-top: 14px;
  }

  /* ── DASHBOARD ── */
  .dashboard-section {
    display: block;
    width: 100%;
    background: linear-gradient(160deg, #0a0f1e 0%, #0d1a2e 60%, #0a1520 100%);
    padding: 60px 60px;
    position: relative; overflow: hidden;
  }
  .dashboard-text { max-width: 80%; }
  .dashboard-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 40px; align-items: start; margin-top: 60px;
  }
  /* Copy left, waveform right — one card, split internally. The card owns the
     full row so the wave pane keeps a readable aspect ratio; the insight
     panels sit underneath rather than squeezing it. */
  .dashboard-main-card {
    display: grid;
    grid-template-columns: 38fr 62fr;
    border-radius: 24px; overflow: hidden; position: relative;
    border: 1px solid rgba(43,179,163,0.2);
    animation: glowPulse 3s ease-in-out infinite;
    min-height: 540px;
    background:
      radial-gradient(ellipse 130% 95% at 50% 50%, rgba(43,179,163,0.075) 0%, transparent 62%),
      linear-gradient(155deg, #0b1424 0%, #070c18 55%, #050912 100%);
  }
  .dash-card-copy {
    display: flex; flex-direction: column; justify-content: center;
    padding: 48px 40px; min-width: 0;
  }
  /* The waveform canvas has no intrinsic size, so this pane sets its box. */
  .dash-card-wave {
    position: relative; overflow: hidden; min-width: 0;
    border-left: 1px solid rgba(43,179,163,0.13);
  }
  .wave-canvas {
    position: absolute; inset: 0;
    width: 100%; height: 100%;
    display: block;
  }

  /* ── WAVE CALLOUTS ── */
  .wave-callouts {
    position: absolute; inset: 0;
    pointer-events: none; z-index: 2;
    opacity: 0; transition: opacity 0.6s ease 0.35s;
  }
  .wc-anchor { position: absolute; top: 0; left: 0; height: 100%; width: 0; will-change: transform; }
  .wc-card {
    position: absolute; top: 0; left: 0;
    display: inline-block; white-space: nowrap;
    padding: 6px 10px 7px;
    border-radius: 9px;
    background: rgba(6,11,22,0.9);
    border: 1px solid rgba(255,255,255,0.11);
    box-shadow: 0 6px 18px rgba(0,0,0,0.45);
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
    will-change: transform;
  }
  .wc-label {
    display: flex; align-items: center; gap: 6px;
    font-family: 'Inter', sans-serif; font-size: 11px; font-weight: 700;
    color: #ffffff; line-height: 1.2;
  }
  .wc-swatch { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; }
  .wc-value {
    font-family: 'Inter', sans-serif; font-size: 11px; font-weight: 700;
    line-height: 1.3; margin-top: 2px; padding-left: 11px;
    font-variant-numeric: tabular-nums;
  }
  .wc-line { position: absolute; left: 0; width: 1px; transform: translateX(-50%); }
  .wc-dot {
    position: absolute; top: 0; left: 0;
    width: 7px; height: 7px; border-radius: 50%;
    will-change: transform;
  }
  /* Three cards side by side need most of the panel width. Below 700px they
     stay, but shrink and stack label over value so the widest card ("Engagement")
     is narrower than the gap between two lane centres — see WaveCallouts, which
     also clamps each card inside the panel so none is clipped at the edges. */
  @media (max-width: 700px) {
    .wc-card { padding: 4px 7px 5px; border-radius: 7px; }
    .wc-label { font-size: 9px; gap: 4px; }
    .wc-swatch { width: 5px; height: 5px; }
    .wc-value { font-size: 9px; padding-left: 9px; margin-top: 1px; }
    .wc-dot { width: 6px; height: 6px; }
  }
  @media (max-width: 420px) {
    .wc-card { padding: 3px 5px 4px; border-radius: 6px; }
    .wc-label { font-size: 8px; gap: 3px; }
    .wc-value { font-size: 8px; padding-left: 0; }
  }

  /* ── DASHBOARD INTRO BLOCK (inside .dashboard-main-card) ── */
  .dash-eyebrow-rule {
    display: block; width: 40px; height: 2px; border-radius: 2px;
    background: #2bb3a3; margin-bottom: 14px;
  }
  .dash-eyebrow-text {
    display: block;
    font-family: 'Inter', sans-serif; font-size: 12px; font-weight: 700;
    letter-spacing: 2.4px; text-transform: uppercase; color: #2bb3a3;
  }
  .dash-intro-title {
    margin-top: 18px;
    font-family: 'Inter', sans-serif; font-weight: 700;
    font-size: 32px; line-height: 1.18; color: #f1f5f9;
  }
  .dash-intro-grad {
    background: linear-gradient(90deg, #2bb3a3 0%, #6b8cf5 55%, #a78bfa 100%);
    -webkit-background-clip: text; -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  .dash-intro-sub {
    margin-top: 14px;
    font-size: 16px; line-height: 1.6; font-weight: 400;
    color: rgba(148,163,184,0.85);
  }
  /* Stacked, not a 3-up row: the copy pane is a column now, so each feature
     reads as chip + text on one line. */
  .dash-feature-row {
    display: flex; flex-direction: column;
    gap: 20px; margin-top: 30px;
  }
  .dash-feature { display: flex; align-items: flex-start; gap: 14px; }
  .dash-feature-chip {
    width: 38px; height: 38px; border-radius: 11px; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
  }
  .dash-feature-chip svg { width: 18px; height: 18px; display: block; }
  .dash-feature-copy { min-width: 0; }
  .dash-feature-title {
    font-family: 'Inter', sans-serif; font-size: 15px; font-weight: 700;
    color: #ffffff; margin-bottom: 4px;
  }
  .dash-feature-desc {
    font-size: 13.5px; line-height: 1.55;
    color: rgba(148,163,184,0.8);
  }
  @media (max-width: 1280px) {
    .dash-intro-title { font-size: 28px; }
    .dash-card-copy { padding: 40px 32px; }
    .dash-feature-row { gap: 18px; margin-top: 26px; }
  }
  @media (max-width: 768px) {
    .dash-intro-title { font-size: 26px; }
    .dash-intro-sub { font-size: 15px; }
  }
  .scan-overlay {
    position: absolute; inset: 0; pointer-events: none;
  }
  .scan-line-h {
    position: absolute; left: 0; right: 0; height: 2px;
    background: linear-gradient(90deg, transparent, #2bb3a3, transparent);
    animation: scanLine 3.5s ease-in-out infinite;
    box-shadow: 0 0 12px rgba(43,179,163,0.8);
    top: 8%;
  }
  .scan-corner {
    position: absolute; width: 20px; height: 20px;
    border-color: #2bb3a3; border-style: solid; border-width: 0;
  }
  .scan-corner.tl { top: 16px; left: 16px; border-top-width: 2px; border-left-width: 2px; }
  .scan-corner.tr { top: 16px; right: 16px; border-top-width: 2px; border-right-width: 2px; }
  .scan-corner.bl { bottom: 16px; left: 16px; border-bottom-width: 2px; border-left-width: 2px; }
  .scan-corner.br { bottom: 16px; right: 16px; border-bottom-width: 2px; border-right-width: 2px; }

  .dashboard-hud {
    position: absolute; bottom: 0; left: 0; right: 0;
    padding: 20px 24px;
    background: linear-gradient(0deg, rgba(10,15,30,0.9) 0%, transparent 100%);
    display: flex; gap: 20px; align-items: flex-end;
  }
  .hud-metric { text-align: center; }
  .hud-metric-val {
    font-size: 28px; font-family: 'Inter', sans-serif; font-weight: 800;
    background: linear-gradient(90deg, #2bb3a3, #6366f1);
    -webkit-background-clip: text; -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  .hud-metric-label {
    font-size: 20px; color: rgba(255,255,255,0.5);
    text-transform: uppercase; letter-spacing: 1px;
  }

  /* ── INSIGHT CARDS ── */
  .insights-panel {
    display: grid; grid-template-columns: repeat(3, 1fr);
    gap: 16px; align-items: start;
  }
  /* The rail's hover nudge slid cards sideways; in a row it reads as a lift. */
  .insights-panel .insight-card:hover { transform: translateY(-4px); }
  .insight-card {
    background: rgba(255,255,255,0.04);
    border: 1px solid rgba(43,179,163,0.15);
    border-radius: 18px; padding: 22px 24px;
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    transition: all 0.3s;
  }
  .insight-card:hover {
    background: rgba(43,179,163,0.08);
    border-color: rgba(43,179,163,0.4);
    transform: translateX(4px);
  }
  .insight-card-title {
    font-family: 'Inter', sans-serif; font-size: 14px; font-weight: 600;
    color: #2bb3a3; letter-spacing: 2px; text-transform: uppercase; margin-bottom: 14px;
  }
  .metric-row { display: flex; align-items: center; gap: 12px; margin-bottom: 10px; }
  .metric-label { font-size: 14px; color: rgba(255,255,255,0.6); width: 90px; flex-shrink: 0; }
  .metric-bar-bg {
    flex: 1; height: 7px; border-radius: 10px;
    background: rgba(255,255,255,0.06);
    overflow: hidden;
  }
  .metric-bar-fill {
    height: 100%; border-radius: 10px;
    background: linear-gradient(90deg, #2bb3a3, #6366f1);
    animation: barGrow 1.5s ease-out both;
    animation-delay: var(--bar-delay, 0s);
  }
  .metric-val { font-size: 14px; font-weight: 700; color: white; width: 36px; text-align: right; }

  /* ── HEATMAP ── */
  .heatmap-grid {
    display: grid; grid-template-columns: repeat(7, 1fr); gap: 5px;
    margin-top: 8px;
  }
  .heatmap-cell {
    aspect-ratio: 1; border-radius: 4px;
    animation: heatPulse 2s ease-in-out infinite;
    animation-delay: var(--heat-delay, 0s);
  }
  .heatmap-legend {
    display: flex; align-items: center; gap: 8px; margin-top: 10px;
    font-size: 20px; color: rgba(255,255,255,0.4);
  }
  .heatmap-legend-bar {
    flex: 1; height: 5px; border-radius: 4px;
    background: linear-gradient(90deg, rgba(43,179,163,0.15), #2bb3a3, #6366f1);
  }

  /* ── RISK SIGNALS ── */
  .risk-row {
    display: flex; justify-content: space-between; align-items: center;
    margin-bottom: 10px; font-size: 20px;
  }
  .risk-label { color: rgba(255,255,255,0.55); }
  .risk-clear { color: #2bb3a3; font-weight: 600; font-size: 20px; }
  .risk-warn  { color: #f59e0b; font-weight: 600; font-size: 20px; }

  /* ── FEATURES GRID ── */
  .features-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 18px; margin-top: 56px;
  }
  .feature-card {
    background: white;
    border: 1.5px solid rgba(43,179,163,0.1);
    border-radius: 20px; padding: 32px 24px;
    transition: all 0.35s cubic-bezier(0.34,1.56,0.64,1);
    position: relative; overflow: hidden; cursor: default;
  }
  .feature-card::before {
    content: '';
    position: absolute; top: 0; left: 0; right: 0; height: 3px;
    background: linear-gradient(90deg, #2bb3a3, #6366f1);
    transform: scaleX(0); transform-origin: left;
    transition: transform 0.4s ease;
  }
  .feature-card:hover::before { transform: scaleX(1); }
  .feature-card:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 50px rgba(43,179,163,0.18);
    border-color: rgba(43,179,163,0.35);
  }
  .feature-icon-wrap {
    width: 52px; height: 52px; border-radius: 14px; margin-bottom: 18px;
    display: flex; align-items: center; justify-content: center;
    background: linear-gradient(135deg, rgba(43,179,163,0.12), rgba(99,102,241,0.08));
    font-size: 24px; transition: transform 0.3s;
  }
  .feature-card:hover .feature-icon-wrap { transform: scale(1.12) rotate(-4deg); }
  .feature-title {
    font-family: 'Inter', sans-serif; font-size: 24px; font-weight: 700;
    color: #0f172a; margin-bottom: 8px;
  }
  .feature-desc { font-size: 20px; color: #64748b; line-height: 1.8; }

  /* ── PROCESS CARDS ── */
  .process-section { background: #f8faff; }
  .process-grid {
    display: grid; grid-template-columns: repeat(4, 1fr);
    gap: 14px; margin-top: 56px; position: relative;
  }
  .process-connector {
    position: absolute; top: 42px; left: 12.5%; right: 12.5%; height: 2px;
    background: linear-gradient(90deg, #2bb3a3, #6366f1);
    z-index: 0; overflow: hidden;
  }
  .process-connector::after {
    content: '';
    position: absolute; inset: 0;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.7), transparent);
    animation: shimmer 2.5s linear infinite;
    background-size: 200% auto;
  }
  .process-card {
    background: white; border-radius: 20px; padding: 32px 22px;
    border: 1.5px solid rgba(43,179,163,0.1);
    text-align: center;
    transition: all 0.3s; position: relative; z-index: 1;
    cursor: default;
  }
  .process-card:hover {
    transform: translateY(-6px);
    box-shadow: 0 16px 40px rgba(43,179,163,0.15);
  }
  .process-num {
    width: 52px; height: 52px; border-radius: 50%;
    background: linear-gradient(135deg, #2bb3a3, #6366f1);
    color: white; font-family: 'Inter', sans-serif;
    font-weight: 800; font-size: 18px;
    display: flex; align-items: center; justify-content: center;
    margin: 0 auto 18px;
    box-shadow: 0 6px 20px rgba(43,179,163,0.35);
  }
  .process-title {
    font-family: 'Inter', sans-serif; font-size: 17px; font-weight: 700;
    color: #0f172a; margin-bottom: 8px;
  }
  .process-desc { font-size: 20px; color: #64748b; line-height: 1.65; }

  /* ── CTA SECTION ── */
  .cta-section {
    display: block;
    width: 100%;
    background: linear-gradient(135deg, #0a0f1e 0%, #0d1a2e 100%);
    padding: 100px 60px; text-align: center; position: relative; overflow: hidden;
  }
  .cta-glow {
    position: absolute; width: 600px; height: 600px; border-radius: 50%;
    background: radial-gradient(circle, rgba(43,179,163,0.12) 0%, transparent 70%);
    top: 50%; left: 50%; transform: translate(-50%,-50%);
    pointer-events: none;
  }
  .cta-title {
    font-family: 'Inter', sans-serif; font-size: clamp(32px, 4vw, 56px);
    font-weight: 800; color: white; letter-spacing: -1.5px;
    position: relative;
  }
  .cta-gradient-text {
    background: linear-gradient(90deg, #2bb3a3, #6366f1);
    -webkit-background-clip: text; -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  .cta-sub { font-size: 17px; color: rgba(255,255,255,0.5); margin-top: 16px; position: relative; }
  .cta-btn-wrap { margin-top: 44px; position: relative; }

  /* ── MODAL ── */
  .modal-overlay {
    position: fixed; inset: 0; z-index: 200;
    background: rgba(10,15,30,0.75); backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    display: flex; align-items: center; justify-content: center;
    animation: fadeIn 0.2s ease;
    padding: 20px;
  }
  .modal-box {
    background: white; border-radius: 24px; padding: 48px 44px;
    width: 100%; max-width: 440px;
    box-shadow: 0 30px 80px rgba(43,179,163,0.2);
    animation: fadeUp 0.35s ease;
    position: relative;
  }
  .modal-close {
    position: absolute; top: 20px; right: 24px;
    background: none; border: none; font-size: 22px; cursor: pointer;
    color: #94a3b8; transition: color 0.2s, transform 0.2s;
    line-height: 1;
  }
  .modal-close:hover { color: #2bb3a3; transform: rotate(90deg); }
  .modal-title {
    font-family: 'Inter', sans-serif; font-size: 28px; font-weight: 800;
    background: linear-gradient(90deg, #2bb3a3, #6366f1);
    -webkit-background-clip: text; -webkit-text-fill-color: transparent;
    background-clip: text;
    margin-bottom: 28px;
  }
  .form-group { margin-bottom: 20px; }
  .form-label { display: block; font-size: 13px; font-weight: 500; color: #475569; margin-bottom: 7px; }
  .form-input {
    width: 100%; padding: 13px 16px; border-radius: 10px;
    border: 1.5px solid #e2e8f0; font-family: 'Inter', 'DM Sans', sans-serif;
    font-size: 14px; transition: all 0.2s; outline: none;
    background: white; color: #1e293b;
  }
  .form-input:focus { border-color: #2bb3a3; box-shadow: 0 0 0 3px rgba(43,179,163,0.12); }
  .form-submit {
    width: 100%; padding: 14px; border-radius: 10px; border: none;
    background: linear-gradient(90deg, #2bb3a3, #6366f1);
    color: white; font-family: 'Inter', 'DM Sans', sans-serif;
    font-weight: 600; font-size: 15px; cursor: pointer;
    transition: all 0.3s; margin-top: 8px;
  }
  .form-submit:hover { transform: translateY(-2px); box-shadow: 0 10px 30px rgba(43,179,163,0.35); }
  .form-success { text-align: center; padding: 20px 0; }
  .form-success-icon { font-size: 48px; margin-bottom: 16px; }
  .form-success-title {
    font-family: 'Inter', sans-serif; font-size: 22px; font-weight: 800;
    color: #2bb3a3; margin-bottom: 8px;
  }
  .form-success-sub { font-size: 14px; color: #64748b; }

  /* ── PS-GRID (PROCESS) ── */
  .ps-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
    padding: 2rem 0;
  }
  .ps-card {
    position: relative;
    background: #ffffff;
    border-radius: 20px;
    padding: 32px 22px 28px;
    text-align: center;
    cursor: default;
    border: 1px solid rgba(43,179,163,0.13);
    overflow: hidden;
    transition: transform 0.5s cubic-bezier(0.23,1,0.32,1),
                box-shadow 0.5s cubic-bezier(0.23,1,0.32,1),
                border-color 0.5s ease;
    isolation: isolate;
  }
  .ps-card::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background: radial-gradient(ellipse 80% 60% at var(--mx,50%) var(--my,50%),
      rgba(43,179,163,0.13) 0%, transparent 70%);
    opacity: 0;
    transition: opacity 0.4s ease;
    pointer-events: none;
    z-index: 0;
  }
  .ps-card:hover::before { opacity: 1; }
  .ps-border-glow {
    position: absolute;
    inset: 0;
    border-radius: inherit;
    padding: 1px;
    background: linear-gradient(135deg, rgba(43,179,163,0), rgba(43,179,163,0.9), rgba(99,102,241,0.7), rgba(43,179,163,0));
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    opacity: 0;
    transition: opacity 0.5s ease;
    background-size: 200% 200%;
    animation: rotateBorder 3s linear infinite paused;
    pointer-events: none;
    z-index: 0;
  }
  .ps-card:hover .ps-border-glow {
    opacity: 1;
    animation-play-state: running;
  }
  @keyframes rotateBorder {
    0%   { background-position: 0% 0%; }
    50%  { background-position: 100% 100%; }
    100% { background-position: 0% 0%; }
  }
  .ps-card:hover {
    transform: translateY(-10px) scale(1.03);
    box-shadow: 0 2px 4px rgba(43,179,163,0.05), 0 8px 24px rgba(43,179,163,0.12), 0 24px 60px rgba(43,179,163,0.14), 0 0 0 1px rgba(43,179,163,0.2);
    border-color: transparent;
    z-index: 3;
  }
  .ps-num-wrap { position: relative; width: 54px; height: 54px; margin: 0 auto 20px; }
  .ps-num-bg {
    position: absolute; inset: 0; border-radius: 50%;
    background: linear-gradient(135deg, #2bb3a3, #6366f1);
    transition: transform 0.5s cubic-bezier(0.23,1,0.32,1), box-shadow 0.5s ease;
  }
  .ps-card:hover .ps-num-bg {
    transform: scale(1.18);
    box-shadow: 0 0 0 6px rgba(43,179,163,0.12), 0 0 0 12px rgba(43,179,163,0.05);
  }
  .ps-num-ring {
    position: absolute; inset: -5px; border-radius: 50%;
    border: 1.5px solid rgba(43,179,163,0.25);
    opacity: 0; transform: scale(0.8);
    transition: opacity 0.4s ease 0.05s, transform 0.5s cubic-bezier(0.23,1,0.32,1) 0.05s;
  }
  .ps-card:hover .ps-num-ring { opacity: 1; transform: scale(1); }
  .ps-num {
    position: relative; z-index: 1;
    font-family: 'Inter', sans-serif; font-weight: 600; font-size: 22px;
    color: white; line-height: 54px; display: block;
  }
  .ps-title {
    font-family: 'Inter', sans-serif; font-size: 18px; font-weight: 600;
    color: #0f172a; margin-bottom: 10px; position: relative; z-index: 1;
    transition: color 0.35s ease;
  }
  .ps-card:hover .ps-title { color: #1a7a72; }
  .ps-desc {
    font-size: 16px; color: #64748b; line-height: 1.7;
    position: relative; z-index: 1; transition: color 0.35s ease;
  }
  .ps-card:hover .ps-desc { color: #475569; }
  .ps-tag {
    display: inline-block; margin-top: 14px; padding: 4px 12px;
    border-radius: 100px; font-size: 11px; font-weight: 600;
    letter-spacing: 0.5px; text-transform: uppercase;
    background: rgba(43,179,163,0.08); color: #2bb3a3;
    border: 1px solid rgba(43,179,163,0.2);
    opacity: 0; transform: translateY(6px);
    transition: opacity 0.35s ease 0.05s, transform 0.35s ease 0.05s;
    position: relative; z-index: 1;
  }
  .ps-card:hover .ps-tag { opacity: 1; transform: translateY(0); }

  /* ═══════════════════════════════════════════════
     RESPONSIVE BREAKPOINTS
     Desktop (>1280px): unchanged
     Small laptop (1025px–1280px): minor tweaks
     Tablet (769px–1024px): drawer, 2-col grids
     Large phone (481px–768px): 1-col, scaled type
     Small phone (≤480px): compact spacing
  ═══════════════════════════════════════════════ */

  /* ── SMALL LAPTOP (1025–1280px) ── */
  @media (max-width: 1280px) and (min-width: 1025px) {
    .section { padding: 80px 40px; }
    .dashboard-section { padding: 60px 40px; }
    .dashboard-grid { gap: 28px; }
    .dashboard-main-card { min-height: 480px; }
    .features-grid { grid-template-columns: repeat(4, 1fr); gap: 14px; }
    .cta-section { padding: 80px 40px; }
    .hero h1 { max-width: 72%; }
    .hero p { max-width: 72%; font-size: 26px; }
    .footer { padding: 32px 40px; }
  }

  /* ── TABLET (769–1024px) ── */
  @media (max-width: 1024px) {
    .section { padding: 70px 28px; }
    .dashboard-section { padding: 60px 28px; }
    .dashboard-grid { gap: 28px; }
    /* Too narrow to split: copy stacks over the wave, which keeps its own
       aspect ratio so the callouts still have room to sit on the crests. */
    .dashboard-main-card { grid-template-columns: 1fr; min-height: 0; }
    .dash-card-copy { padding: 36px 28px 30px; }
    .dash-card-wave { border-left: none; border-top: 1px solid rgba(43,179,163,0.13); aspect-ratio: 16 / 9; }
    .insights-panel { grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); }
    .dashboard-text { max-width: 100%; }

    .features-grid { grid-template-columns: repeat(2, 1fr); gap: 16px; }
    .ps-grid { grid-template-columns: repeat(2, 1fr); }
    .process-grid { grid-template-columns: repeat(2, 1fr); }
    .process-connector { display: none; }
    .footer-grid-3col { grid-template-columns: 1fr 1fr !important; }

    .cta-section { padding: 80px 28px; }

    .hero { padding: 120px 28px 70px; }
    .hero-coming-soon { top: 145px; right: 28px; font-size: 11px; padding: 7px 15px; }
    .hero h1 { max-width: 86%; }
    .hero p { max-width: 86%; font-size: 22px; }
    .hero-stats { gap: 28px; }

    .section-subtitle { max-width: 100%; font-size: 18px; }
    .feature-title { font-size: 20px; }
    .feature-desc { font-size: 17px; }
  }

  /* ── LARGE PHONE (481–768px) ── */
  @media (max-width: 768px) {
    .hero { padding: 110px 20px 60px; min-height: auto; }
    /* Absolute placement collides with the h1 once the heading is full-width,
       so below tablet the badge rejoins the flow and sits above the heading. */
    .hero-coming-soon {
      position: static; top: auto; right: auto;
      margin: 0 auto 18px; font-size: 10px; padding: 6px 13px; letter-spacing: 1px;
    }
    .hero-coming-soon-icon { width: 12px; height: 12px; }
    .hero-inner { min-height: auto; gap: 0; }
    .hero h1 { max-width: 100%; font-size: clamp(28px, 8vw, 48px); }
    .hero p { max-width: 100%; font-size: 17px; margin-top: 14px; }
    .hero-cta-group { flex-direction: column; align-items: stretch; gap: 12px; margin-top: 28px; }
    .btn-primary, .btn-ghost { font-size: 16px; padding: 15px 24px; width: 100%; }
    .hero-stats { gap: 20px; margin-top: 40px; }
    .hero-stat-num { font-size: 28px; }
    .hero-stat-label { font-size: 12px; }

    .section { padding: 56px 18px; }
    .section-title { font-size: clamp(26px, 7vw, 38px); letter-spacing: -0.8px; }
    .section-subtitle { font-size: 16px; }

    .dashboard-section { padding: 56px 18px; }
    .dashboard-grid { margin-top: 36px; }
    .dashboard-main-card { border-radius: 16px; }
    .dash-card-copy { padding: 30px 22px 26px; }
    .dash-card-wave { aspect-ratio: 4 / 3; }
    .insights-panel { grid-template-columns: 1fr; }

    .features-grid { grid-template-columns: 1fr; gap: 14px; margin-top: 36px; }
    .feature-card { padding: 24px 20px; }
    .feature-title { font-size: 18px; }
    .feature-desc { font-size: 15px; }
    .feature-icon-wrap { width: 44px; height: 44px; font-size: 20px; }

    .ps-grid { grid-template-columns: 1fr; gap: 14px; padding: 1.5rem 0; }
    .ps-card { padding: 26px 20px 22px; }

    .insight-card { padding: 18px 16px; }
    .metric-label { font-size: 13px; width: 80px; }
    .metric-val { font-size: 13px; }
    .risk-row { font-size: 15px; }
    .risk-clear, .risk-warn { font-size: 15px; }
    .heatmap-legend { font-size: 13px; }

    .cta-section { padding: 70px 20px; }
    .cta-title { letter-spacing: -0.8px; }
    .cta-sub { font-size: 15px; }
    .cta-btn-wrap { margin-top: 32px; }

    /* FAQ section */
    section[style*="padding: 100px 60px"] { padding: 60px 20px !important; }
  }

  /* ── SMALL PHONE (≤480px) ── */
  @media (max-width: 480px) {
    .hero { padding: 96px 16px 50px; }
    .hero-coming-soon { margin-bottom: 14px; font-size: 9px; padding: 5px 11px; gap: 6px; }
    .hero-coming-soon-icon { width: 11px; height: 11px; }
    .hero h1 { font-size: clamp(24px, 9vw, 36px); }
    .hero p { font-size: 15px; }
    .hero-stat-num { font-size: 24px; }
    .hero-stats { gap: 16px; }

    .section { padding: 48px 16px; }
    .section-title { font-size: clamp(22px, 8vw, 32px); }
    .section-subtitle { font-size: 15px; }

    .dashboard-section { padding: 48px 16px; }

    .feature-card { padding: 20px 16px; border-radius: 16px; }
    .feature-title { font-size: 16px; }
    .feature-desc { font-size: 14px; }

    .ps-card { padding: 22px 16px 20px; border-radius: 16px; }
    .ps-title { font-size: 16px; }
    .ps-desc { font-size: 14px; }

    .cta-section { padding: 56px 16px; }
    .cta-sub { font-size: 14px; }

    .insight-card { padding: 16px 14px; }
    .metric-label { width: 72px; font-size: 12px; }
  }

  /* ── LANDSCAPE PHONE ── */
  @media (max-height: 500px) and (orientation: landscape) {
    .hero { padding-top: 80px; min-height: auto; }
    .hero-inner { min-height: auto; }
  }

  /* ── IMAGE RESPONSIVE HELPERS ── */
  img { max-width: 100%; height: auto; }
`;

// ─── Data ─────────────────────────────────────────────────────────────────────

const HEATMAP_VALUES = [
  [0.2,0.4,0.7,0.9,0.8,0.5,0.3],
  [0.3,0.6,0.8,1.0,0.9,0.7,0.4],
  [0.1,0.3,0.6,0.8,0.7,0.5,0.2],
  [0.4,0.7,0.9,0.9,0.8,0.6,0.3],
  [0.2,0.5,0.7,0.8,0.9,0.7,0.4],
];

function heatColor(v) {
  if (v < 0.3) return `rgba(43,179,163,${(0.15 + v * 0.5).toFixed(2)})`;
  if (v < 0.6) return `rgba(43,179,163,${(0.3 + v * 0.5).toFixed(2)})`;
  if (v < 0.8) return `rgba(99,102,241,${(0.5 + v * 0.3).toFixed(2)})`;
  return `rgba(99,102,241,${Math.min(0.95, 0.7 + v * 0.25).toFixed(2)})`;
}

const FEATURES = [
  { icon: "◎", title: "Behaviour Intelligence", desc: "Continuously monitor and interpret individual behavioural patterns using computer vision and adaptive ML models." },
  { icon: "▦", title: "Real-time Analytics", desc: "Live dashboards surface Platforms dynamics as they unfold  no post-processing delays." },
  { icon: "◈", title: "Emotional Mapping", desc: "Detect micro-expressions and affective signals to understand emotional climate at scale." },
  { icon: "△", title: "Risk Detection", desc: "Early-warning flags for disengagement, distress, and behavioural anomalies before they escalate." },
];

// ─── Dashboard intro block ────────────────────────────────────────────────────

const IconBars = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 20v-7" />
    <path d="M12 20V4" />
    <path d="M18 20v-11" />
  </svg>
);

const IconHeart = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19.5 9c0 4.6-7.5 10-7.5 10S4.5 13.6 4.5 9a3.8 3.8 0 0 1 7.5-1.3A3.8 3.8 0 0 1 19.5 9z" />
  </svg>
);

const IconShield = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3l7.5 3.2v5.1c0 4.6-3.2 8.7-7.5 9.9-4.3-1.2-7.5-5.3-7.5-9.9V6.2L12 3z" />
    <path d="M9.4 12.1l1.9 1.9 3.5-3.6" />
  </svg>
);

const DASH_INTRO_FEATURES = [
  { accent: "#2bb3a3", Icon: IconBars,   title: "Engagement Insights", desc: "See how learners interact and participate over time." },
  { accent: "#a78bfa", Icon: IconHeart,  title: "Emotional Signals",   desc: "Detect early signs of struggle or disengagement." },
  { accent: "#60a5fa", Icon: IconShield, title: "Risk Indicators",     desc: "Identify at-risk learners and enable timely support." },
];

// Add to FEATURES/DATA section at top:
const FAQS = [
  {
    q: "Is Sentio Mind legal to deploy in Indian organizations?",
    a: "Yes. Sentio Mind is designed in compliance with India's Digital Personal Data Protection (DPDP) Act 2023 and applicable IT rules. We work with institutional legal teams to ensure proper consent frameworks, data processing agreements, and stakeholder  notification protocols are in place before any deployment."
  },
  {
    q: "What data is actually collected? Is it stored?",
    a: "Sentio Mind processes visual signals to derive behavioural metrics such as engagement, attention, posture, gaze direction, and emotional indicators. Individuals are assigned system-generated IDs managed by administrators. Face encodings used for re-identification are encrypted before storage. Profile images (face crops), behavioural analytics, and associated metadata are stored in encrypted form. No biometric or behavioural data is shared with third parties, and access controls and retention policies can be managed by authorised administrators."
  },
  {
    q: "Can Individuals opt out?",
    a: "Absolutely. We mandate opt-out mechanisms as part of every institutional deployment. Individuals are informed through your organization's existing communication channels. Individual opt-outs are honoured at the system level  the platform simply excludes opted-out individuals from analysis."
  },
  {

  q: "How does Sentio Mind generate its behavioural insights?",
  a: "Sentio Mind processes real-time visual signals through our proprietary AI models, which are trained to recognise patterns in engagement, attention, and emotional climate. The system continuously refines its understanding of your environment over time, surfacing insights that become richer and more contextually aware with each session. Every insight is designed to be a conversation-starter for your professionals  giving them earlier, richer information than traditional observation methods allow."
},
  {
    q: "Will this replace teachers, counsellors, or psychologists?",
    a: "No  and that is by design. Sentio Mind gives your existing professionals earlier, richer information so they can intervene more effectively. A professional who previously learned about a struggling individual weeks later can now be alerted in days. The platform amplifies human judgment; it does not replace it."
  },
  {
    q: "Is this ethical to use?",
    a: "We take this question seriously. Sentio Mind was built with input from behavioural scientists and  welfare practitioners.The system uses system-generated identifiers for re-identification workflows, and sensitive biometric data such as face encodings and profile imagery are encrypted before storage. Data is not sold or shared with third parties. All deployments require institutional ethics review sign-off, and we publish our ethical usage guidelines publicly."
  },
  {
    q: "What new hardware do we need to install?",
    a: "Typically none. Sentio Mind integrates with your existing CCTV or camera infrastructure. Our AI engine is cloud-deployable or on-premise depending on your data governance requirements  meaning your IT team retains control."
  },
  {
    q: "How is Sentio Mind different from generic surveillance software?",
    a: "Traditional surveillance systems primarily record and monitor footage. Sentio Mind focuses on analysing behavioural signals to generate wellbeing-related insights . No raw video is retained on our servers, and data is not shared with advertisers, law enforcement, or third parties."
  }
];

const PROCESS = [
  { num: "01", title: "Capture Platforms Data", desc: "Non-intrusive camera feeds capture real-time visual data across the learning environment." },
  { num: "02", title: "AI Processing Engine", desc: "Proprietary neural networks process frames in milliseconds, extracting behavioural signals." },
  { num: "03", title: "Insight Generation", desc: "Multi-dimensional models synthesize signals into coherent, actionable intelligence layers." },
  { num: "04", title: "Actionable Reports", desc: "Teams receive clean, interpretable reports with recommended interventions." },
];

const RISK_SIGNALS = [
  { label: "Low distraction zones", value: "All clear", warn: false },
  { label: "Disengagement alerts", value: "2 flagged", warn: true },
  { label: "Emotional anomalies", value: "0 detected", warn: false },
];
 

// ─── Sub-components ───────────────────────────────────────────────────────────

function AnimatedBar({ label, value, delay }) {
  return (
    <div className="metric-row">
      <span className="metric-label">{label}</span>
      <div className="metric-bar-bg">
        <div
          className="metric-bar-fill"
          style={{ "--w": `${value}%`, "--bar-delay": `${delay}s`, width: `${value}%` }}
        />
      </div>
      <span className="metric-val">{value}%</span>
    </div>
  );
}

function Heatmap() {
  return (
    <div className="insight-card">
      <div className="insight-card-title">Engagement Heatmap</div>
      <div className="heatmap-grid">
        {HEATMAP_VALUES.flat().map((v, i) => (
          <div
            key={i}
            className="heatmap-cell"
            style={{ background: heatColor(v), "--heat-delay": `${(i % 7) * 0.08}s` }}
            title={`${Math.round(v * 100)}%`}
          />
        ))}
      </div>
      <div className="heatmap-legend">
        <span>Low</span>
        <div className="heatmap-legend-bar" />
        <span>High</span>
      </div>
      <div style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", marginTop: 6, textAlign: "right" }}>
        Mon – Sun · 5 sessions
      </div>
    </div>
  );
}

 
 
// ─── Main App ─────────────────────────────────────────────────────────────────
// Add this component near your other sub-components:
function FAQSection({ onBookDemo }) {
  const [openIdx, setOpenIdx] = useState(null);
  
  return (
    <section style={{
      background: "#f8faff",
      padding: "100px 60px",
      width: "100%",
      display: "block"
    }}>
      <div className="reveal" style={{ textAlign: "center", marginBottom: 56 }}>
        <h2 className="section-title">Questions We'd Expect<br />
          <span style={{ background: "linear-gradient(90deg,#2bb3a3,#6366f1)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
            From Responsible Educators
          </span>
        </h2>
        <p className="section-subtitle" style={{ margin: "14px auto 0" }}>
          We built Sentio Mind knowing these questions matter. Here are honest answers.
        </p>
      </div>

      <div style={{ maxWidth: 820, margin: "0 auto", display: "flex", flexDirection: "column", gap: 14 }}>
        {FAQS.map((faq, i) => (
          <div
            key={i}
            className="reveal"
            style={{
              background: "white",
              border: `1.5px solid ${openIdx === i ? "rgba(43,179,163,0.45)" : "rgba(43,179,163,0.12)"}`,
              borderRadius: 18,
              overflow: "hidden",
              transition: "border-color 0.3s, box-shadow 0.3s",
              boxShadow: openIdx === i ? "0 8px 32px rgba(43,179,163,0.12)" : "none",
              transitionDelay: `${i * 0.05}s`
            }}
          >
            <button
              onClick={() => setOpenIdx(openIdx === i ? null : i)}
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "22px 28px",
                background: "none",
                border: "none",
                cursor: "pointer",
                textAlign: "left",
                gap: 16
              }}
            >
              <span style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: 16,
                fontWeight: 500,
                color: openIdx === i ? "#2bb3a3" : "#0f172a",
                transition: "color 0.25s",
                lineHeight: 1.5
              }}>
                {faq.q}
              </span>
              <span style={{
                flexShrink: 0,
                width: 28, height: 28,
                borderRadius: "50%",
                background: openIdx === i
                  ? "linear-gradient(135deg,#2bb3a3,#6366f1)"
                  : "rgba(43,179,163,0.1)",
                display: "flex", alignItems: "center", justifyContent: "center",
                color: openIdx === i ? "white" : "#2bb3a3",
                fontSize: 18,
                fontWeight: 300,
                transition: "all 0.3s",
                transform: openIdx === i ? "rotate(45deg)" : "rotate(0deg)"
              }}>
                +
              </span>
            </button>

            {openIdx === i && (
              <div style={{
                padding: "0 28px 24px",
                fontSize: 16,
                color: "#475569",
                lineHeight: 1.8,
                borderTop: "1px solid rgba(43,179,163,0.1)",
                paddingTop: 20,
                animation: "fadeIn 0.25s ease"
              }}>
                
                {faq.a}
                {i === 5 && (
                  <button
                    type="button"
                    style={{
                      display: "inline",
                      marginTop: 12,
                      padding: 0,
                      border: "none",
                      background: "none",
                      color: "#2bb3a3",
                      fontWeight: 600,
                      fontSize: 14,
                      textDecoration: "none",
                      cursor: "pointer"
                    }}
                  >
                    Read our Ethical Usage Guidelines →
                  </button>

                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Trust badges row */}
      <div className="reveal" style={{
        display: "flex",
        gap: 24,
        justifyContent: "center",
        flexWrap: "wrap",
        marginTop: 64
      }}>
        {[
          { label: "DPDP Act 2023 Compliant" },
          { label: "Data Never Shared with Third Parties" },
          { label: "Opt-Out Guaranteed" },
          { label: "Human-Led Decisions Always" },
        ].map(({ icon, label }) => (
          <div key={label} style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "12px 22px",
            borderRadius: 100,
            border: "1.5px solid rgba(43,179,163,0.25)",
            background: "white",
            fontSize: 14,
            fontWeight: 600,
            color: "#475569",
            boxShadow: "0 2px 12px rgba(43,179,163,0.07)"
          }}>
            <span style={{ fontSize: 18 }}>{icon}</span>
            {label}
          </div>
        ))}
      </div>
    </section>
  );
}

export default function SentioMind() {
  const navigate = useNavigate();
  const location = useLocation();
  const [showDemo, setShowDemo] = useState(false);
  const [introComplete, setIntroComplete] = useState(() => hasSeenIntro());
  const [pendingScroll, setPendingScroll] = useState(() => location.state?.scrollTo ?? null);

  useEffect(() => {
    if (showDemo || !introComplete) return;

    const elements = document.querySelectorAll(".reveal");
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -80px 0px" }
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [showDemo, introComplete]);
  useEffect(() => {
    document.body.style.overflow = showDemo ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [showDemo]);
  useEffect(() => {
    // Arrivals carrying a section target scroll there instead of to the top.
    if (!location.state?.scrollTo) window.scrollTo(0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (showDemo || !pendingScroll) return;
    const id = pendingScroll;
    setPendingScroll(null);
    const frame = requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    });
    return () => cancelAnimationFrame(frame);
  }, [showDemo, pendingScroll]);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  const openDemo = useCallback(() => {
    setIntroComplete(true);
    setShowDemo(true);
  }, []);

  const goToMobileApp = useCallback(() => navigate("/"), [navigate]);

  const navigateFromDemo = (sectionId) => {
    setIntroComplete(true);
    setShowDemo(false);
    if (sectionId && sectionId !== "contact") setPendingScroll(sectionId);
  };

  return (
    <>
      {showDemo && (
        <div id="demo-page-root">
          <BookDemo
            onNavigate={navigateFromDemo}
            onBack={() => navigateFromDemo("home")}
            backLabel="Back to Platform"
          />
        </div>
      )}
    <div className="sentio-app" style={showDemo ? { display: "none" } : undefined} aria-hidden={showDemo}>
      <style>{globalStyle}</style>

      {/* ── INTRO SCREEN ── */}
      {!introComplete && <BrandIntro onDone={() => setIntroComplete(true)} />}
    {/* ── NAVBAR + MOBILE DRAWER (shared with the mobile-app page) ── */}
      <SiteNav
        links={NAV_SECTIONS}
        onNavigate={scrollTo}
        onOpenDemo={openDemo}
      />
      {/* ── HERO ───────────────────────────────────────────────────────────── */}
      <section id="home" className="hero" style={{ 
        position: "relative", 
        overflow: "hidden", 
        isolation: "isolate",
        background: "linear-gradient(135deg, #e0f5f3 0%, #f0f9ff 50%, #eef2ff 100%)"
      }}>
        {/* Neural mesh fills entire hero background */}
        <div style={{ 
          position: "absolute", 
          inset: 0, 
          zIndex: 0,
          width: "100%",
          height: "100%"
        }}>
         
        </div>
        
        <div className="hero-coming-soon">
          <svg className="hero-coming-soon-icon" viewBox="0 0 24 24" fill="none" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 7v5l3.5 2" />
          </svg>
          Coming Soon
        </div>

        <div className="hero-bg-orb" style={{ width: 500, height: 500, background: "radial-gradient(circle, rgba(43,179,163,0.12) 0%, transparent 70%)", top: -80, left: -120, zIndex: 1 }} />
        <div className="hero-bg-orb" style={{ width: 400, height: 400, background: "radial-gradient(circle, rgba(99,102,241,0.1) 0%, transparent 70%)", bottom: 40, right: -80, zIndex: 1 }} />

        <div className="hero-inner" style={{ position: "relative", zIndex: 2 }}>
           

          <h1 spellCheck={false}>
            <span style={{ display: "block" ,color:"black"}}>
              Behaviour Intelligence
            </span>

            <span className="hero-gradient-text">for Modern Environments</span>
          </h1>

          <p>Transform real-time interactions into deeply actionable insights using cutting-edge machine learning and behavioural science.</p>

          <div className="hero-cta-group">
            <button
              className="btn-primary"
              onClick={openDemo}
            >
              Have an Enquiry?
            </button>
            <button className="btn-ghost" onClick={goToMobileApp}>Experience the Mobile App</button>
          </div>

         
        </div>
      </section>

      {/* ── DASHBOARD ──────────────────────────────────────────────────────── */}
      <section className="dashboard-section">
        <div style={{ position: "absolute", width: 500, height: 500, borderRadius: "50%", background: "radial-gradient(circle, rgba(43,179,163,0.07) 0%, transparent 70%)", top: -80, right: -80, pointerEvents: "none" }} />

        <div className="reveal">
          <h2 className="section-title" style={{ color: "white" }}>
            Real-Time Behaviour<br />
            <span style={{ background: "linear-gradient(90deg,#2bb3a3,#6366f1)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
              Intelligence Dashboard
            </span>
          </h2>
          <p className="section-subtitle" style={{ color: "rgba(255,255,255,0.45)" }}>
            Visualize engagement, emotional signals, and risk indicators through AI-powered analytics  as it happens.
          </p>
        </div>

        <div className="dashboard-grid reveal"  >
          {/* Main signal card: copy on the left, live waveform on the right */}
          <div className="dashboard-main-card">
            <div className="dash-card-copy">
              <span className="dash-eyebrow-rule" />
              <span className="dash-eyebrow-text">From Signals to Understanding</span>

              <h3 className="dash-intro-title">
                Turn learner behaviour into{" "}
                <span className="dash-intro-grad">meaningful insights</span>
              </h3>

              <p className="dash-intro-sub">
                Uncover engagement patterns, emotional cues, and risk indicators — all in one unified view.
              </p>

              <div className="dash-feature-row">
                {DASH_INTRO_FEATURES.map(({ accent, Icon, title, desc }) => (
                  <div className="dash-feature" key={title}>
                    <div
                      className="dash-feature-chip"
                      style={{ background: `${accent}22`, border: `1px solid ${accent}47`, color: accent }}
                    >
                      <Icon />
                    </div>
                    <div className="dash-feature-copy">
                      <div className="dash-feature-title">{title}</div>
                      <div className="dash-feature-desc">{desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Positioned box the canvas sizes itself against. */}
            <div className="dash-card-wave">
              <DashboardWaveform />
              <div className="scan-overlay">
                <div className="scan-line-h" />
                <div className="scan-corner tl" />
                <div className="scan-corner tr" />
                <div className="scan-corner bl" />
                <div className="scan-corner br" />
              </div>
            </div>
          </div>

          {/* Side panels */}
          <div className="insights-panel">
            <div className="insight-card">
              <div className="insight-card-title">Generating Insights...</div>
              <AnimatedBar label="Engagement"    value={78} delay={0.3} />
              <AnimatedBar label="Focus"         value={72} delay={0.5} />
              <AnimatedBar label="Attention"     value={65} delay={0.7} />
              <AnimatedBar label="Participation" value={81} delay={0.9} />
            </div>

            <Heatmap />

            <div className="insight-card">
              <div className="insight-card-title">Risk Signals</div>
              {RISK_SIGNALS.map(({ label, value, warn }) => (
                <div className="risk-row" key={label}>
                  <span className="risk-label">{label}</span>
                  <span className={warn ? "risk-warn" : "risk-clear"}>{value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── FEATURES ───────────────────────────────────────────────────────── */}
      <section id="platform" className="section" style={{ background: "white" }}>
        <div   className="reveal" style={{ textAlign: "center" }}>
          <div className="section-label">Platform</div>
          <h2 className="section-title">Platform Capabilities</h2>
          <p className="section-subtitle" style={{ margin: "14px auto 0" }}>
            A full-stack intelligence layer built for organizations of any scale.
          </p>
        </div>
        <div className="features-grid">
          {FEATURES.map((f, i) => (
            <div key={f.title} className="feature-card reveal"   style={{ transitionDelay: `${i * 0.1}s` }}>
              <div className="feature-icon-wrap">{f.icon}</div>
              <div className="feature-title">{f.title}</div>
              <div className="feature-desc">{f.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── PROCESS ────────────────────────────────────────────────────────── */}
      <section id="insights" className="section process-section">
        <div   className="reveal" style={{ textAlign: "center" }}>
          <div className="section-label">How It Works</div>
          <h2 className="section-title">
            From Camera Feed<br />
            <span style={{ background: "linear-gradient(90deg,#2bb3a3,#6366f1)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
              to Actionable Insight
            </span>
          </h2>
        </div>
      <div className="ps-grid">
        {PROCESS.map((p, i) => {
          const tags = ["Input layer", "Neural core", "Synthesis", "Output layer"];
          return (
            <div
              key={p.num}
              className="ps-card"
              onMouseMove={e => {
                const r = e.currentTarget.getBoundingClientRect();
                e.currentTarget.style.setProperty('--mx', ((e.clientX - r.left) / r.width * 100).toFixed(1) + '%');
                e.currentTarget.style.setProperty('--my', ((e.clientY - r.top)  / r.height * 100).toFixed(1) + '%');
              }}
              onMouseLeave={e => {
                e.currentTarget.style.setProperty('--mx', '50%');
                e.currentTarget.style.setProperty('--my', '50%');
              }}
            >
              <div className="ps-border-glow" />
              <div className="ps-num-wrap">
                <div className="ps-num-bg" />
                <div className="ps-num-ring" />
                <span className="ps-num">{p.num}</span>
              </div>
              <div className="feature-title">{p.title}</div>
              <div className="feature-desc">{p.desc}</div>
              <span className="ps-tag">{tags[i]}</span>
            </div>
          );
        })}
      </div>
       
        </section>
      {/* ── FAQ ────────────────────────────────────────────────────────────── */}
            {/* ── TRUST & COMPLIANCE ─────────────────────────────────────────────── */}
      <TrustComplianceSection />

      {/* ── FAQ ────────────────────────────────────────────────────────────── */}
      <FAQSection onBookDemo={openDemo} />
      {/* ── CTA ────────────────────────────────────────────────────────────── */}
      <section id="contact" className="cta-section">
        <div className="cta-glow" />
        <div className="reveal">
          <h2 className="cta-title">
            Experience<br />
            <span className="cta-gradient-text">Sentio Mind</span>
          </h2>
          <p className="cta-sub">See how AI transforms your understanding of every individual, every day.</p>
          <div className="cta-btn-wrap">
            <button className="btn-primary" style={{ fontSize: 16, padding: "16px 44px" }} onClick={openDemo}>
              Have an Enquiry?
            </button>
          </div>
        </div>
      </section>
        
      {/* ── FOOTER ─────────────────────────────────────────────────────────── */}
      <SiteFooter />
      
      {/* ── MODAL ──────────────────────────────────────────────────────────── */}
       
    </div>
    </>
  );
}